/**
 * Действия над камнем: связаться с поставщиком и публичная ссылка.
 *
 * Связь — это конец цепочки, ради которой каталог существует: нашёл камень →
 * написал поставщику. Сообщение собирается заранее и по-английски: рынок
 * говорит на нём, а номер камня и SKU поставщика вставлены сразу — чтобы
 * человек не набирал их пальцем в мессенджере.
 *
 * Публичная страница показывает только белый список полей, и он живёт на
 * сервере: клиент даже теоретически не может отправить наружу цену, статус
 * или внутреннюю заметку.
 */
import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { apiFetch } from '../sync';
import type { LocalStone, LocalSupplier } from '../db';

interface Share {
  id: string; created_at: string; expires_at: string | null;
  revoked_at: string | null; opens: number;
}

/** Телефон в вид, который понимают ссылки мессенджеров: только цифры. */
const digits = (v: string) => v.replace(/[^\d]/g, '');

function message(stone: LocalStone, extra?: string): string {
  const sku = stone.supplierSku ? ` (your ref. ${stone.supplierSku})` : '';
  return `Hello! I am interested in stone ${stone.publicNo ?? stone.localNo}${sku} ` +
    `that I viewed at your booth.${extra ? ` ${extra}` : ''}`;
}

// ── связаться с поставщиком ─────────────────────────────────────────────────

export function ContactSheet({ stone, supplier, shareUrl, onClose }: {
  stone: LocalStone;
  supplier: LocalSupplier;
  shareUrl?: string | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState('');
  const text = message(stone, shareUrl ? `Details: ${shareUrl}` : undefined);

  const by = (kind: string) => supplier.contacts.find((c) => c.kind === kind)?.value;
  const phone = by('phone') ?? by('mobile');
  const whatsapp = by('whatsapp') ?? phone;
  const email = by('email');
  const telegram = by('telegram');
  const wechat = by('wechat');

  async function copy(value: string, label: string) {
    try { await navigator.clipboard.writeText(value); setCopied(label); } catch { /* нет доступа */ }
  }

  const enc = encodeURIComponent(text);
  const channels: Array<{ cap: string; sub?: string; href?: string; copyValue?: string }> = [];

  if (whatsapp) channels.push({
    cap: 'WhatsApp', sub: whatsapp,
    href: `https://wa.me/${digits(whatsapp)}?text=${enc}`,
  });
  if (telegram) channels.push({
    cap: 'Telegram', sub: telegram,
    // Ссылка на диалог текст не переносит — он уже в буфере обмена.
    href: `https://t.me/${telegram.replace(/^@/, '')}`,
  });
  if (email) channels.push({
    cap: 'Почта', sub: email,
    href: `mailto:${email}?subject=${encodeURIComponent(`Stone ${stone.publicNo ?? stone.localNo}`)}&body=${enc}`,
  });
  if (phone) {
    channels.push({ cap: 'Позвонить', sub: phone, href: `tel:${digits(phone)}` });
    channels.push({ cap: 'SMS', sub: phone, href: `sms:${digits(phone)}?&body=${enc}` });
  }
  if (wechat) channels.push({
    cap: 'WeChat', sub: `ID: ${wechat} — откроется приложение, ID в буфере`,
    copyValue: wechat, href: 'weixin://',
  });

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <div className="head" style={{ marginTop: 0 }}>
          <h3>Написать про {stone.publicNo ?? stone.localNo}</h3>
          <span>{supplier.companyName}</span>
        </div>

        <div className="memo" style={{ marginTop: 0 }}>
          <q>{text}</q>
        </div>
        <button className="btn btn-secondary" style={{ width: '100%', marginTop: 10 }}
                onClick={() => void copy(text, 'текст')}>
          {copied === 'текст' ? 'Скопировано' : 'Скопировать текст'}
        </button>

        {channels.length === 0 ? (
          <div className="blankslate">
            <b>У поставщика нет контактов</b>
            Откройте его карточку и добавьте телефон или почту — или переснимите визитку.
          </div>
        ) : (
          <div className="list" style={{ marginTop: 12 }}>
            {channels.map((c) => (
              <a key={c.cap} className="row" href={c.href}
                 target={c.href?.startsWith('http') ? '_blank' : undefined} rel="noreferrer"
                 style={{ textDecoration: 'none' }}
                 onClick={() => { if (c.copyValue) void copy(c.copyValue, c.cap); else void copy(text, 'текст'); }}>
                <span className="grow">
                  <span className="nm">{c.cap}</span>
                  {c.sub && <span className="sub">{c.sub}</span>}
                </span>
                <span className="chev">›</span>
              </a>
            ))}
          </div>
        )}

        <p className="meta" style={{ marginTop: 10 }}>
          Текст сообщения копируется при выборе канала — если мессенджер
          не подставит его сам, просто вставьте.
        </p>
      </div>
    </>
  );
}

// ── публичная ссылка ────────────────────────────────────────────────────────

const EXPIRY: Array<{ cap: string; days: number | null }> = [
  { cap: '7 дней', days: 7 },
  { cap: '30 дней', days: 30 },
  { cap: 'Бессрочно', days: null },
];

export function ShareSheet({ stone, onClose, onCreated }: {
  stone: LocalStone;
  onClose: () => void;
  onCreated?: (url: string) => void;
}) {
  const [shares, setShares] = useState<Share[]>([]);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState('');

  async function load() {
    try {
      const res = await apiFetch(`/api/stones/${stone.id}/shares`);
      if (res.ok) setShares(((await res.json()) as { shares: Share[] }).shares);
    } catch { /* без сети список недоступен */ }
  }
  useEffect(() => { void load(); }, [stone.id]);

  const live = shares.filter((s) => !s.revoked_at
    && (!s.expires_at || new Date(s.expires_at) > new Date()));
  const url = live[0] ? `https://gemsnote.app/s/${live[0].id}` : '';

  // QR ведёт на публичную страницу: наклейка на пакетике открывает камень
  // любому, у кого есть камера — без приложения и без ключа.
  useEffect(() => {
    if (!url) { setQr(''); return; }
    void QRCode.toDataURL(url, { width: 480, margin: 1 }).then(setQr).catch(() => setQr(''));
  }, [url]);

  async function create(days: number | null) {
    setBusy(true); setProblem('');
    try {
      const res = await apiFetch(`/api/stones/${stone.id}/share`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ days }),
      });
      if (!res.ok) throw new Error(`сервер ответил ${res.status}`);
      const d = (await res.json()) as { url: string };
      onCreated?.(d.url);
      await load();
    } catch (e: any) {
      setProblem(`Не получилось: ${e?.message ?? e}. Ссылка делается сервером — нужна связь.`);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    await apiFetch(`/api/shares/${id}/revoke`, { method: 'POST' });
    await load();
  }

  async function copy() {
    try { await navigator.clipboard.writeText(url); setCopied(url); } catch { /* ignore */ }
  }

  const fmt = (d: string) => new Date(d).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' });

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <div className="head" style={{ marginTop: 0 }}>
          <h3>Публичная страница</h3>
          <span>{stone.publicNo ?? stone.localNo}</span>
        </div>

        {problem && <div className="memo warn" style={{ marginTop: 0 }}>{problem}</div>}

        {url ? (
          <>
            <div className="memo" style={{ marginTop: 0 }}>
              <b>{url.replace('https://', '')}</b>
              {live[0].expires_at
                ? <><br />Работает до {fmt(live[0].expires_at)}.</>
                : <><br />Бессрочная.</>}
              {live[0].opens > 0 && <> Открывали: {live[0].opens}.</>}
            </div>
            {qr && (
              <div className="qrbox">
                <img src={qr} alt="QR-код страницы камня" />
                <span>Наклейка на пакетик: камера телефона откроет страницу без приложения</span>
              </div>
            )}
            <div className="dock" style={{ position: 'static', padding: '12px 0 0' }}>
              <button className="btn btn-secondary" onClick={() => void revoke(live[0].id)}>Отозвать</button>
              <button className="btn btn-primary" onClick={() => void copy()}>
                {copied ? 'Скопирована' : 'Скопировать'}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="memo" style={{ marginTop: 0 }}>
              Страница покажет фото и паспорт камня: вид, вес, цвет, происхождение,
              сертификат, ваш номер и SKU поставщика. <b>Цен, статусов и заметок
              на ней не бывает</b> — список полей задан на сервере.
            </div>
            <div className="head"><h3>Срок действия</h3><span /></div>
            <div className="states" style={{ marginTop: 0 }}>
              {EXPIRY.map((e) => (
                <button key={e.cap} className="state-btn" disabled={busy}
                        onClick={() => void create(e.days)}>
                  {e.cap}
                </button>
              ))}
            </div>
          </>
        )}

        {shares.some((s) => s.revoked_at) && (
          <p className="meta" style={{ marginTop: 12 }}>
            Отозванные ссылки перестают открываться сразу, у всех.
          </p>
        )}
      </div>
    </>
  );
}

// ── подборка для покупателя ─────────────────────────────────────────────────

/**
 * Несколько отобранных камней — одна ссылка для покупателя.
 * Правила те же, что у одиночной страницы: белый список полей на сервере,
 * срок, отзыв. Название необязательно, но помогает покупателю понять,
 * что перед ним: «Сапфиры до 3 карат», а не безымянный список.
 */
export function CollectionSheet({ ids, onClose, onDone }: {
  ids: string[];
  onClose: () => void;
  onDone?: () => void;
}) {
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [made, setMade] = useState<{ url: string; count: number } | null>(null);
  const [qr, setQr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!made) return;
    void QRCode.toDataURL(made.url, { width: 480, margin: 1 }).then(setQr).catch(() => setQr(''));
  }, [made]);

  async function create(days: number | null) {
    setBusy(true); setProblem('');
    try {
      const res = await apiFetch('/api/collections', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stoneIds: ids, title: title.trim() || undefined, days }),
      });
      if (!res.ok) throw new Error(`сервер ответил ${res.status}`);
      setMade((await res.json()) as { url: string; count: number });
    } catch (e: any) {
      setProblem(`Не получилось: ${e?.message ?? e}. Подборку делает сервер — нужна связь.`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="backdrop" onClick={onClose} />
      <div className="sheet">
        <div className="grip" />
        <div className="head" style={{ marginTop: 0 }}>
          <h3>Подборка покупателю</h3>
          <span>{ids.length}</span>
        </div>

        {problem && <div className="memo warn" style={{ marginTop: 0 }}>{problem}</div>}

        {made ? (
          <>
            <div className="memo" style={{ marginTop: 0 }}>
              <b>{made.url.replace('https://', '')}</b>
              <br />{made.count} камн{made.count === 1 ? 'ень' : made.count < 5 ? 'я' : 'ей'} ·
              покупатель видит фото и паспорт, без цен и заметок.
            </div>
            {qr && (
              <div className="qrbox">
                <img src={qr} alt="QR-код подборки" />
                <span>Покажите с экрана — камера телефона откроет витрину</span>
              </div>
            )}
            <div className="dock" style={{ position: 'static', padding: '12px 0 0' }}>
              <button className="btn btn-secondary" onClick={() => { onDone?.(); onClose(); }}>Готово</button>
              <button className="btn btn-primary" onClick={() => {
                void navigator.clipboard.writeText(made.url).then(() => setCopied(true)).catch(() => {});
              }}>
                {copied ? 'Скопирована' : 'Скопировать'}
              </button>
            </div>
          </>
        ) : (
          <>
            <label className="field" style={{ marginTop: 4 }}>
              <span>Название — его увидит покупатель</span>
              <input value={title} onChange={(e) => setTitle(e.target.value)}
                     placeholder="Например: Сапфиры до 3 карат" />
            </label>
            <div className="head"><h3>Срок действия</h3><span /></div>
            <div className="states" style={{ marginTop: 0 }}>
              {EXPIRY.map((e) => (
                <button key={e.cap} className="state-btn" disabled={busy}
                        onClick={() => void create(e.days)}>
                  {e.cap}
                </button>
              ))}
            </div>
            <p className="meta" style={{ marginTop: 10 }}>
              Отозвать можно в любой момент: Ещё → Подборки покупателям.
            </p>
          </>
        )}
      </div>
    </>
  );
}
